import {Box, Flex, Image, Text} from 'exsportia-components'
import React, {FC} from 'react'
import {NavigateFunction} from 'react-router-dom'

import {AppearanceTemplateElement, StoreCardData} from '../../../api/display-settings/types'
import {useVenue} from '../../../api/venue/hooks'
import {TranslatorScope} from '../../../locale/types'
import {LocaleOptions} from '../../../utils/i18n'

export type StoreProductItemProps = {
  l: (scope: TranslatorScope, options?: LocaleOptions) => string
  item: AppearanceTemplateElement
  navigate: NavigateFunction
  isMemberBlocked?: boolean
  variant?: 'card' | 'row'
  /** [mobile, desktop] widths — the section's column count decides desktop. */
  cardWidth?: string | [string, string]
}

// A PRODUCT section item on the venue page (WBA-4516): image + name + price
// (single or min–max across variants), red sold-out line when unavailable.
// `card` renders the store catalogue's tile instead of a row — a section of
// only products looks like the store (Dima 2026-08-19).
export const StoreProductItem: FC<StoreProductItemProps> = ({
  l,
  item,
  navigate,
  isMemberBlocked,
  variant = 'row',
  cardWidth,
}) => {
  const itemData = item.data as unknown as StoreCardData
  const {data: venue} = useVenue()
  const symbolic = (value: number | null) =>
    value === null ? '' : l(`${venue?.currency || ''}_symbolic`, {value})
  const price =
    itemData.priceMin === null
      ? ''
      : itemData.priceMin === itemData.priceMax
      ? symbolic(itemData.priceMin)
      : `${symbolic(itemData.priceMin)} – ${symbolic(itemData.priceMax)}`
  if (variant === 'card') {
    return (
      <Flex
        width={cardWidth ?? ['calc(50% - 8px)', 'calc(33.333% - 11px)']}
        flexDirection='column'
        gap='8px'
        cursor={isMemberBlocked ? 'default' : 'pointer'}
        onClick={isMemberBlocked ? undefined : () => navigate(`./store/goods/${item.entityId}`)}
      >
        <Box
          width='100%'
          pt='100%'
          position='relative'
          borderRadius='16px'
          overflow='hidden'
          backgroundColor='rgba(0, 16, 61, 0.06)'
        >
          {itemData.thumbUrl && (
            <Box
              position='absolute'
              top='0'
              left='0'
              width='100%'
              height='100%'
            >
              <Image
                width='100%'
                height='100%'
                style={{objectFit: 'cover'}}
                src={itemData.thumbUrl}
              />
            </Box>
          )}
        </Box>
        <Text text={itemData.name} />
        <Text
          text={itemData.inStock ? price : l('store-sold-out', {caseAction: 'capitalize'})}
          settings={{fontWeight: 600, color: itemData.inStock ? undefined : '#B3261E'}}
        />
      </Flex>
    )
  }
  return (
    <Flex
      width='100%'
      gap='12px'
      alignItems='center'
      cursor={isMemberBlocked ? 'default' : 'pointer'}
      onClick={isMemberBlocked ? undefined : () => navigate(`./store/goods/${item.entityId}`)}
    >
      {/* Photos are optional for store items (WBA-4512), and an empty src painted
          a broken tile here. Same tinted-placeholder pattern as the store grid. */}
      <Box
        width='48px'
        height='48px'
        flex='none'
        overflow='hidden'
        borderRadius='12px'
        backgroundColor='rgba(0, 16, 61, 0.06)'
      >
        {/* `size='cover'` is not a real prop — it feeds styled-system
            width/height, leaving object-fit: fill, i.e. a stretched photo. */}
        {itemData.thumbUrl && (
          <Image
            width='100%'
            height='100%'
            style={{objectFit: 'cover'}}
            src={itemData.thumbUrl}
          />
        )}
      </Box>
      <Flex
        flexDirection='column'
        minWidth='0'
      >
        <Text text={itemData.name} />
        <Text
          text={itemData.inStock ? price : l('store-sold-out', {caseAction: 'capitalize'})}
          settings={{fontSize: '13px', color: itemData.inStock ? undefined : '#B3261E'}}
        />
      </Flex>
    </Flex>
  )
}
